import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Popup from '/@/components/popup';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import Drawing from '../drawing';
import colors from '../theme-provider/colors';
import styles from './styles.module.css';

colors;

const getLocalePrefixFromURL = () => {
    let { pathname } = window.location;
    if (!pathname.startsWith('/')) pathname = '/'.concat(pathname);
    return pathname.split('/')[1];
};

interface ErrorPopupProps {
    /**
     * If true, renders as a full-page error instead of a modal popup
     */
    fullPage?: boolean;
    /**
     * Custom title for the error message
     */
    title?: string;
    /**
     * Custom description for the error message
     */
    description?: string;
    /**
     * Type of drawing to show ('error' or 'not-found')
     */
    drawingType?: 'error' | 'not-found';
    /**
     * Show a "Try again" button that calls onReset
     */
    showResetButton?: boolean;
    /**
     * Callback for the reset button
     */
    onReset?: () => void;
    /**
     * Show a "Go back" button
     */
    showGoBackButton?: boolean;
    /**
     * Callback for the go back button
     */
    onGoBack?: () => void;
    /**
     * Show development error details
     */
    errorDetails?: React.ReactNode;
}

const ErrorPopup = ({
    fullPage = false,
    title = _('Ocurrió un error inesperado'),
    description = _('Actualizá la página o intentá de nuevo.'),
    drawingType = 'error',
    showResetButton = false,
    onReset,
    showGoBackButton = false,
    onGoBack,
    errorDetails,
}: ErrorPopupProps) => {
    const handleRecovery = () => {
        const localePrefix = getLocalePrefixFromURL();
        window.location.replace(`${window.location.origin}/${localePrefix}/home/`);
    };

    const errorContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Drawing size='lg' type={drawingType} />
            <Typography color='primary' variant='h5'>
                {title}
            </Typography>
            <Typography color='primary.mui.inactive' variant='subtitle2' sx={{ mb: 2 }}>
                {description}
            </Typography>
            <div className={styles.buttons}>
                <Stack direction='row' spacing={2} justifyContent='center'>
                    {showGoBackButton && onGoBack && (
                        <Button
                            variant='outlined'
                            onClick={onGoBack}
                            sx={{
                                borderColor: (colors.themes as any)[(settings as any).app.theme]?.primary?.default,
                                color: (colors.themes as any)[(settings as any).app.theme]?.primary?.default,
                            }}
                        >
                            {_('Volver')}
                        </Button>
                    )}
                    {showResetButton && onReset && (
                        <Button
                            variant='outlined'
                            onClick={onReset}
                            sx={{
                                borderColor: (colors.themes as any)[(settings as any).app.theme]?.primary?.default,
                                color: (colors.themes as any)[(settings as any).app.theme]?.primary?.default,
                            }}
                        >
                            {_('Intentar de nuevo')}
                        </Button>
                    )}
                    <Button
                        variant='contained'
                        onClick={handleRecovery}
                        sx={{
                            backgroundColor: (colors.themes as any)[(settings as any).app.theme]?.primary?.default,
                            '&:hover': {
                                backgroundColor: (colors.themes as any)[(settings as any).app.theme]?.primary?.default,
                            },
                        }}
                    >
                        {_('Volver a Inicio')}
                    </Button>
                </Stack>
            </div>
        </Box>
    );

    if (fullPage) {
        return (
            <Box className={styles.fullPageRoot}>
                <Stack spacing={3} alignItems='center' justifyContent='center' sx={{ minHeight: '100vh', p: 3 }}>
                    {errorContent}
                </Stack>
            </Box>
        );
    }

    return (
        <Popup visible onClose={handleRecovery}>
            {errorContent}
        </Popup>
    );
};

export default ErrorPopup;
